import React, { useEffect, useState } from "react";
import { Clock, PlayCircle, ShieldCheck, MailWarning } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const DEFAULTS = { enabled: true, mode: "interval", interval_hours: 1, run_hour: 0, run_minute: 15, grace_days: 0, warn_enabled: true, warn_days: 7 };

export default function AdminMembership() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    api.get("/admin/settings/membership_expiry")
      .then((r) => setCfg({ ...DEFAULTS, ...(r.data || {}) }))
      .catch(() => setCfg({ ...DEFAULTS }));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings/membership_expiry", { value: cfg });
      toast.success("Membership settings saved — applied live");
    } catch {
      toast.error("Could not save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/membership/expire-now");
      toast.success(`Sweep complete — ${data.expired} membership(s) expired`);
    } catch {
      toast.error("Could not run the sweep.");
    } finally {
      setRunning(false);
    }
  };

  if (!cfg) return <p className="text-muted-foreground">Loading…</p>;
  const set = (k, v) => setCfg({ ...cfg, [k]: v });

  return (
    <div data-testid="admin-membership">
      <h1 className="font-display text-3xl md:text-4xl font-medium text-primary mb-1">Membership Expiry</h1>
      <p className="text-muted-foreground text-sm mb-8 max-w-2xl">
        Automatically expire paid memberships once their renewal date passes. Expired members are blocked from signing in
        until they renew. Configure how often the check runs below.
      </p>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-card border border-primary/10 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-5"><Clock size={18} className="text-secondary" /><h2 className="font-display text-xl font-semibold text-primary">Schedule</h2></div>

          <label className="flex items-center gap-3 mb-5 cursor-pointer">
            <input type="checkbox" data-testid="expiry-enabled" checked={!!cfg.enabled} onChange={(e) => set("enabled", e.target.checked)} className="h-4 w-4 accent-[hsl(var(--secondary))]" />
            <span className="text-sm font-medium text-primary">Enable automatic expiry checks</span>
          </label>

          <div className="mb-5">
            <label className="text-xs text-muted-foreground">How often to run</label>
            <div className="flex gap-2 mt-1.5">
              <button type="button" data-testid="expiry-mode-interval" onClick={() => set("mode", "interval")} className={`flex-1 rounded-xl border px-3 py-2 text-sm font-medium transition-colors ${cfg.mode === "interval" ? "bg-secondary text-secondary-foreground border-secondary" : "border-input bg-background text-foreground"}`}>Every N hours</button>
              <button type="button" data-testid="expiry-mode-daily" onClick={() => set("mode", "daily")} className={`flex-1 rounded-xl border px-3 py-2 text-sm font-medium transition-colors ${cfg.mode === "daily" ? "bg-secondary text-secondary-foreground border-secondary" : "border-input bg-background text-foreground"}`}>Once daily</button>
            </div>
          </div>

          {cfg.mode === "interval" ? (
            <div>
              <label className="text-xs text-muted-foreground">Run every (hours)</label>
              <input type="number" min={1} max={168} data-testid="expiry-interval-hours" value={cfg.interval_hours} onChange={(e) => set("interval_hours", Math.max(1, parseInt(e.target.value || "1", 10)))} className="w-full mt-1 rounded-xl border border-input bg-background px-3 py-2 text-sm" />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Hour (UTC, 0–23)</label>
                <input type="number" min={0} max={23} data-testid="expiry-run-hour" value={cfg.run_hour} onChange={(e) => set("run_hour", Math.min(23, Math.max(0, parseInt(e.target.value || "0", 10))))} className="w-full mt-1 rounded-xl border border-input bg-background px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Minute (0–59)</label>
                <input type="number" min={0} max={59} data-testid="expiry-run-minute" value={cfg.run_minute} onChange={(e) => set("run_minute", Math.min(59, Math.max(0, parseInt(e.target.value || "0", 10))))} className="w-full mt-1 rounded-xl border border-input bg-background px-3 py-2 text-sm" />
              </div>
            </div>
          )}

          <button onClick={save} disabled={saving} data-testid="expiry-save" className="mt-6 rounded-full bg-secondary text-secondary-foreground px-6 py-2.5 text-sm font-semibold hover:opacity-90 disabled:opacity-60">
            {saving ? "Saving…" : "Save schedule"}
          </button>

          <div className="mt-7 pt-6 border-t border-primary/10">
            <label className="text-xs text-muted-foreground">Grace period after renewal date (days)</label>
            <input type="number" min={0} max={90} data-testid="expiry-grace-days" value={cfg.grace_days} onChange={(e) => set("grace_days", Math.min(90, Math.max(0, parseInt(e.target.value || "0", 10))))} className="w-full mt-1 rounded-xl border border-input bg-background px-3 py-2 text-sm" />
            <p className="text-xs text-muted-foreground mt-1.5">Members keep access for this many days past their renewal date before being expired. 0 = expire exactly on the renewal date.</p>
          </div>
        </div>

        <div className="bg-card border border-primary/10 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-5"><MailWarning size={18} className="text-secondary" /><h2 className="font-display text-xl font-semibold text-primary">Expiry warning emails</h2></div>
          <label className="flex items-center gap-3 mb-5 cursor-pointer">
            <input type="checkbox" data-testid="warn-enabled" checked={!!cfg.warn_enabled} onChange={(e) => set("warn_enabled", e.target.checked)} className="h-4 w-4 accent-[hsl(var(--secondary))]" />
            <span className="text-sm font-medium text-primary">Email members before their membership renews</span>
          </label>
          <div>
            <label className="text-xs text-muted-foreground">Send warning this many days before renewal</label>
            <input type="number" min={0} max={60} data-testid="warn-days" value={cfg.warn_days} disabled={!cfg.warn_enabled} onChange={(e) => set("warn_days", Math.min(60, Math.max(0, parseInt(e.target.value || "0", 10))))} className="w-full mt-1 rounded-xl border border-input bg-background px-3 py-2 text-sm disabled:opacity-50" />
          </div>
          <p className="text-xs text-muted-foreground mt-3">Uses the editable "Renewal reminder" email template. Sent once per renewal cycle during the daily 13:00 UTC run (requires SMTP configured in Integrations).</p>
          <button onClick={save} disabled={saving} data-testid="warn-save" className="mt-5 rounded-full bg-secondary text-secondary-foreground px-6 py-2.5 text-sm font-semibold hover:opacity-90 disabled:opacity-60">
            {saving ? "Saving…" : "Save warnings"}
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <div className="bg-card border border-primary/10 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4"><ShieldCheck size={18} className="text-secondary" /><h2 className="font-display text-xl font-semibold text-primary">How it works</h2></div>
          <ul className="text-sm text-foreground/70 space-y-2.5 list-disc pl-5">
            <li>Paid members whose renewal date has passed (plus any grace period) are marked <strong>expired</strong>.</li>
            <li>Expired members cannot sign in until they renew from the login screen.</li>
            <li>Active members see an "expiring soon" renew panel on their dashboard and can renew early.</li>
            <li>Free (Explorer) members never expire.</li>
            <li>Changes apply immediately — no restart needed.</li>
          </ul>

          <div className="mt-6 pt-6 border-t border-primary/10">
            <p className="text-sm font-medium text-primary mb-2">Run a sweep right now</p>
            <p className="text-xs text-muted-foreground mb-4">Immediately expire any memberships that are already past their renewal date (plus grace).</p>
            <button onClick={runNow} disabled={running} data-testid="expiry-run-now" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-2.5 text-sm font-semibold hover:bg-secondary transition-colors disabled:opacity-60">
              <PlayCircle size={16} /> {running ? "Running…" : "Run expiry check now"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
